import * as Sentry from '@sentry/react';

import { isValidEnvVar } from '@src/corelib/utils/envUtils';

function getConfig(params) {
  const { context = {} } = params || {};
  return {
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'unknown',
    // Session replay is deliberately NOT listed here. It records the visitor's
    // session and therefore requires consent; the consent registry adds it via
    // enableReplay() once the 'analytics' category is granted.
    //
    // Error and performance capture stay unconditional — they are defensible as
    // legitimate interest for site reliability, and must be running before the
    // app mounts in order to catch bootstrap failures.
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.tanstackRouterBrowserTracingIntegration(context.router),
    ],
    // Tracing
    tracesSampleRate: 0.1, // Capture 10% of the transactions
  };
}

function shouldInit() {
  return import.meta.env.MODE === 'production' && isValidEnvVar('VITE_SENTRY_DSN');
}

function init(params) {
  if (shouldInit()) {
    console.info(`Init | Sentry - ${Sentry.SDK_VERSION}`);
    const config = getConfig(params);
    Sentry.init(config);
  }
}

/**
 * Adds session replay to an already-initialised Sentry client.
 *
 * Called by the consent registry on 'analytics' grant, which may happen mid-
 * session, so this uses addIntegration rather than a second Sentry.init.
 *
 * The sample rate is 0.5 rather than the 0.1 used before gating: consenting
 * visitors are already a self-selected minority, so sampling them at 10% again
 * would record roughly 2-3% of sessions — too sparse to debug from.
 */
async function enableReplay() {
  if (!shouldInit()) {
    return;
  }

  const client = Sentry.getClient();
  if (!client) {
    // init() never ran (no DSN, or a non-production build): nothing to attach to.
    return;
  }

  console.info('Init | Sentry session replay (analytics consent granted)');

  const options = client.getOptions();
  options.replaysSessionSampleRate = 0.5;
  options.replaysOnErrorSampleRate = 1.0;

  // Dynamic import direct from @sentry-internal/replay (~210KB), not from
  // @sentry/react. @sentry/react's index does `export * from '@sentry/browser'`,
  // and @sentry/browser re-exports replayIntegration from this same
  // package -- but since @sentry/react is ALSO imported statically above
  // (for browserTracingIntegration/init/etc.), a dynamic import of that
  // same specifier just resolves to the already-eager chunk; verified via
  // ANALYZE=1 build that it does NOT split. Importing the internal package
  // directly does split it into its own chunk, fetched only when a
  // visitor actually grants analytics consent. @sentry-internal/replay is
  // a direct dependency (see package.json) pinned to the exact version
  // @sentry/browser depends on -- keep that pin in lockstep whenever
  // @sentry/react is upgraded.
  const { replayIntegration } = await import('@sentry-internal/replay');

  Sentry.addIntegration(
    replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  );
}

export { enableReplay, getConfig, init, shouldInit };
